import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../lib/api';
import { 
  Lightbulb, 
  Check, 
  X,
  Filter,
  Building2,
  User,
  Clock,
  CheckCircle2,
  XCircle,
  Loader2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { apiErrorMessage } from '../../lib/utils';

export default function SuggestedItems() {
  const { user } = useAuth();
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewAction, setReviewAction] = useState('approve'); // approve | reject
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewing, setReviewing] = useState(false);

  const fetchSuggestions = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (typeFilter !== 'all') params.item_type = typeFilter;
      
      const response = await api.get('/platform/suggested-items', { params });
      setSuggestions(response.data.suggestions || []);
    } catch (error) {
      toast.error('Failed to load suggestions');
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter]);


  useEffect(() => {
    fetchSuggestions();
  }, [statusFilter, typeFilter, fetchSuggestions]);
  const openReviewDialog = (suggestion, action) => {
    setSelectedSuggestion(suggestion);
    setReviewAction(action);
    setReviewNotes('');
    setReviewDialogOpen(true);
  };

  const handleReview = async () => {
    if (!selectedSuggestion) return;
    
    setReviewing(true);
    try {
      const endpoint = reviewAction === 'approve' 
        ? `/platform/approve-suggestion/${selectedSuggestion.id}`
        : `/platform/reject-suggestion/${selectedSuggestion.id}`;
      
      const response = await api.post(endpoint, { notes: reviewNotes });
      
      toast.success(response.data.message);
      setReviewDialogOpen(false);
      setSelectedSuggestion(null);
      setReviewNotes('');
      fetchSuggestions();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to process suggestion'));
    } finally {
      setReviewing(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-700">Pending Review</Badge>;
      case 'approved':
        return <Badge className="bg-emerald-100 text-emerald-700">Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700">Rejected</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      'medication': 'Medication',
      'lab_test': 'Lab Test',
      'imaging_test': 'Imaging Test',
      'procedure': 'Procedure'
    };
    return labels[type] || type;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (user?.institution_id) {
    return (
      <div className="p-8">
        <Card>
          <CardContent className="py-12 text-center">
            <Lightbulb className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h2 className="text-xl font-semibold text-slate-700 mb-2">Platform Administrator Only</h2>
            <p className="text-slate-500">
              Suggested items approval queue is only accessible to platform administrators.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Lightbulb className="w-6 h-6 text-amber-500" />
          Suggested Master Data Items
        </h1>
        <p className="text-slate-500 mt-1">
          Review and approve user-suggested items for global master data
        </p>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Item Type</Label>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="medication">Medications</SelectItem>
                  <SelectItem value="lab_test">Lab Tests</SelectItem>
                  <SelectItem value="imaging_test">Imaging Tests</SelectItem>
                  <SelectItem value="procedure">Procedures</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-end">
              <Button onClick={fetchSuggestions} variant="outline" className="w-full">
                <Filter className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Suggestions List */}
      <Card>
        <CardHeader>
          <CardTitle>Suggestions ({suggestions.length})</CardTitle>
          <CardDescription>
            Items suggested by clinical staff awaiting approval
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-red-500" />
            </div>
          ) : suggestions.length === 0 ? (
            <div className="text-center py-12">
              <Lightbulb className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">No suggestions found</p>
            </div>
          ) : (
            <div className="space-y-4">
              {suggestions.map((suggestion) => (
                <div
                  key={suggestion.id}
                  className="border rounded-lg p-4 hover:border-red-300 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-slate-900">
                          {suggestion.item_name}
                        </h3>
                        <Badge variant="outline" className="capitalize">
                          {getTypeLabel(suggestion.item_type)}
                        </Badge>
                        {getStatusBadge(suggestion.status)}
                      </div>
                      
                      <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm text-slate-600 mb-3">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4" />
                          <span>Suggested by: {suggestion.suggested_by_name} ({suggestion.suggested_by_role})</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4" />
                          <span>{suggestion.institution_name || 'Unknown Institution'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          <span>{formatDate(suggestion.created_at)}</span>
                        </div>
                        {suggestion.reviewed_at && (
                          <div className="flex items-center gap-2">
                            {suggestion.status === 'approved' ? (
                              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                            ) : (
                              <XCircle className="w-4 h-4 text-red-600" />
                            )}
                            <span>Reviewed: {formatDate(suggestion.reviewed_at)}</span>
                          </div>
                        )}
                      </div>
                      
                      {suggestion.review_notes && (
                        <div className="bg-slate-50 rounded p-3 text-sm">
                          <p className="font-medium text-slate-700 mb-1">Review Notes:</p>
                          <p className="text-slate-600">{suggestion.review_notes}</p>
                        </div>
                      )}
                    </div>
                    
                    {suggestion.status === 'pending' && (
                      <div className="flex gap-2 ml-4">
                        <Button
                          onClick={() => openReviewDialog(suggestion, 'approve')}
                          className="bg-emerald-600 hover:bg-emerald-700"
                          size="sm"
                        >
                          <Check className="w-4 h-4 mr-1" />
                          Approve
                        </Button>
                        <Button
                          onClick={() => openReviewDialog(suggestion, 'reject')}
                          variant="destructive"
                          size="sm"
                        >
                          <X className="w-4 h-4 mr-1" />
                          Reject
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {reviewAction === 'approve' ? 'Approve Suggestion' : 'Reject Suggestion'}
            </DialogTitle>
            <DialogDescription>
              {selectedSuggestion && (
                <span>
                  {getTypeLabel(selectedSuggestion.item_type)}: <strong>{selectedSuggestion.item_name}</strong>
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Label htmlFor="review-notes">
              {reviewAction === 'approve' ? 'Approval Notes (Optional)' : 'Rejection Reason'}
            </Label>
            <Textarea
              id="review-notes"
              value={reviewNotes}
              onChange={(e) => setReviewNotes(e.target.value)}
              placeholder={
                reviewAction === 'approve'
                  ? 'Add any notes about this approval...'
                  : 'Please provide a reason for rejection...'
              }
              rows={3}
              className="mt-2"
            />
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setReviewDialogOpen(false)}
              disabled={reviewing}
            >
              Cancel
            </Button>
            <Button
              onClick={handleReview}
              disabled={reviewing}
              className={reviewAction === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
              variant={reviewAction === 'reject' ? 'destructive' : 'default'}
            >
              {reviewing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  {reviewAction === 'approve' ? (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Approve & Add to Master Data
                    </>
                  ) : (
                    <>
                      <X className="w-4 h-4 mr-2" />
                      Reject Suggestion
                    </>
                  )}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
